import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState, UpgradeGate, PriorityBadge } from "@/components/shared";
import { TASK_CATEGORIES, TASK_STATUS, PRIORITIES, labelFor } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ListChecks, Plus, Trash2, CalendarPlus, Copy, Check, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TINT = {
  not_started: "bg-kid-mist text-kid-muted", in_progress: "bg-kid-sky/15 text-[#2E7CB0]",
  waiting: "bg-kid-sun/20 text-[#B07A11]", completed: "bg-kid-green/15 text-[#3f9d5c]", not_applicable: "bg-kid-mist text-kid-muted",
};

export default function Tasks() {
  const { isPaid } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => { const { data } = await api.get("/tasks"); setTasks(data); setLoading(false); };
  useEffect(() => { if (isPaid) load(); else setLoading(false); }, [isPaid]);

  if (!isPaid) return (<div><PageHeader eyebrow="Task tracker" title="Tasks" /><UpgradeGate title="The Task Tracker is included in Family Path" body="Upgrade to track applications, documents, school meetings and follow-ups." /></div>);
  if (loading) return <Spinner label="Loading tasks…" />;

  const updateStatus = async (t, status) => {
    await api.patch(`/tasks/${t.id}`, { status });
    setTasks((ts) => ts.map((x) => (x.id === t.id ? { ...x, status } : x)));
  };
  const del = async (t) => { await api.delete(`/tasks/${t.id}`); toast.success("Task deleted."); load(); };

  return (
    <div>
      <PageHeader eyebrow="Task tracker" title="Tasks" subtitle="Track applications, documents, meetings and follow-ups."
        action={<button onClick={() => setModalOpen(true)} className="kid-btn kid-btn-primary h-11 px-5" data-testid="tasks-create-open"><Plus size={16} /> Create task</button>} />

      <CalendarSync />
      <ReminderPreview />

      {tasks.length === 0 ? (
        <EmptyState icon={ListChecks} testid="tasks-empty" title="No tasks created yet"
          body="Create tasks from your roadmap to track applications, documents, school meetings, and follow-ups."
          action={<button onClick={() => setModalOpen(true)} className="kid-btn kid-btn-primary h-12 px-7"><Plus size={16} /> Create task</button>} />
      ) : (
        <div className="space-y-3 kid-stagger">
          {tasks.map((t) => (
            <div key={t.id} className="kid-card p-5 flex flex-wrap items-center gap-4" data-testid="task-card">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 mb-1"><PriorityBadge priority={t.priority} /><span className="text-xs text-kid-muted">{labelFor(TASK_CATEGORIES, t.category)}</span></div>
                <h4 className={`font-semibold text-kid-navy ${t.status === "completed" ? "line-through opacity-60" : ""}`}>{t.title}</h4>
                {t.description && <p className="text-sm text-kid-muted mt-0.5">{t.description}</p>}
              </div>
              <Select value={t.status} onValueChange={(v) => updateStatus(t, v)}>
                <SelectTrigger className={`h-9 w-40 rounded-full border-0 text-xs font-poppins font-semibold ${STATUS_TINT[t.status]}`} data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => del(t)} className="text-kid-muted hover:text-kid-coral p-2" data-testid="task-delete"><Trash2 size={17} /></button>
            </div>
          ))}
        </div>
      )}

      <TaskModal open={modalOpen} onClose={() => setModalOpen(false)} onDone={() => { setModalOpen(false); load(); }} />
    </div>
  );
}

function CalendarSync() {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const feedUrl = token ? `${BACKEND_URL}/api/tasks/calendar/${token}.ics` : "";
  const webcalUrl = feedUrl.replace(/^https?:\/\//, "webcal://");
  const googleUrl = feedUrl ? `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}` : "";

  const loadToken = async () => {
    if (token) return;
    try { const { data } = await api.get("/tasks/calendar-subscription"); setToken(data.token); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async () => { const next = !open; setOpen(next); if (next) await loadToken(); };

  const copy = async () => {
    try { await navigator.clipboard.writeText(feedUrl); setCopied(true); toast.success("Calendar link copied."); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Couldn't copy — select and copy the link manually."); }
  };

  const reset = async () => {
    setBusy(true);
    try { const { data } = await api.post("/tasks/calendar-subscription/reset"); setToken(data.token); toast.success("New calendar link created. The old one no longer works."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="kid-card p-5 mb-5" data-testid="calendar-sync">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-kid-teal/10 text-kid-teal grid place-items-center shrink-0"><CalendarPlus size={20} /></div>
        <div className="flex-1 min-w-[220px]">
          <h4 className="font-semibold text-kid-navy">Sync tasks to your calendar</h4>
          <p className="text-sm text-kid-muted">Add your dated tasks to Apple, Google or Outlook Calendar — they stay in sync automatically.</p>
        </div>
        <button onClick={toggle} className="kid-btn kid-btn-ghost h-10 px-4" data-testid="calendar-sync-toggle">
          {open ? "Hide" : "Set up sync"}
        </button>
      </div>

      {open && (
        <div className="mt-5 pt-5 border-t border-kid-line space-y-4" data-testid="calendar-sync-panel">
          <div>
            <Label className="text-xs">Your private calendar link</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              <input readOnly value={feedUrl} onFocus={(e) => e.target.select()} data-testid="calendar-feed-url"
                className="flex-1 min-w-[220px] h-11 rounded-xl border border-kid-line bg-kid-mist px-3 text-sm text-kid-navy font-mono" />
              <button onClick={copy} className="kid-btn kid-btn-primary h-11 px-4" data-testid="calendar-copy-btn">
                {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <a href={webcalUrl} className="kid-btn kid-btn-ghost h-11 px-4" data-testid="calendar-apple-btn">Add to Apple / Outlook</a>
            <a href={googleUrl} target="_blank" rel="noopener noreferrer" className="kid-btn kid-btn-ghost h-11 px-4" data-testid="calendar-google-btn">Add to Google Calendar</a>
            <button onClick={reset} disabled={busy} className="kid-btn kid-btn-ghost h-11 px-4 text-kid-muted ml-auto" data-testid="calendar-reset-btn">
              <RefreshCw size={15} /> Reset link
            </button>
          </div>

          <p className="text-xs text-kid-muted">
            Keep this link private — anyone with it can see your task list. Only tasks with a due date appear. Google Calendar: choose “From URL” and paste the link if the button doesn’t open.
          </p>
        </div>
      )}
    </div>
  );
}

function ReminderPreview() {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tasks/send-reminder-preview");
      toast.success(data.task_count > 0
        ? `Preview sent to your email — ${data.task_count} task${data.task_count !== 1 ? "s" : ""} due this week.`
        : "Preview sent to your email — no tasks due in the next 7 days.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="kid-card p-5 mb-5" data-testid="reminder-preview">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-kid-sun/20 text-[#B07A11] grid place-items-center shrink-0"><Mail size={20} /></div>
        <div className="flex-1 min-w-[220px]">
          <h4 className="font-semibold text-kid-navy">Weekly reminder emails</h4>
          <p className="text-sm text-kid-muted">Get a Monday email of tasks due that week. Manage this in <Link to="/app/account" className="text-kid-teal font-semibold">Account</Link>.</p>
        </div>
        <button onClick={send} disabled={busy} className="kid-btn kid-btn-ghost h-10 px-4" data-testid="reminder-preview-btn">
          {busy ? "Sending…" : "Send me a preview"}
        </button>
      </div>
    </div>
  );
}

function TaskModal({ open, onClose, onDone }) {
  const [form, setForm] = useState({ title: "", description: "", category: "general_next_step", priority: "medium", due_date: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!form.title.trim()) { setError("Please add a task title."); return; }
    setBusy(true);
    try { await api.post("/tasks", { ...form, due_date: form.due_date || null }); toast.success("Task created."); setForm({ title: "", description: "", category: "general_next_step", priority: "medium", due_date: "" }); onDone(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="task-dialog">
        <DialogHeader><DialogTitle>Create task</DialogTitle>
          <DialogDescription>Add a task to track an application, document, meeting or follow-up.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {error && <div className="text-sm text-kid-coral bg-red-50 border border-red-100 rounded-xl px-3 py-2">{error}</div>}
          <div className="space-y-1.5"><Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11 rounded-xl" data-testid="task-title" placeholder="e.g. Gather diagnosis report" /></div>
          <div className="space-y-1.5"><Label>Description <span className="text-xs text-kid-muted font-normal">optional</span></Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-11 rounded-xl" data-testid="task-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="task-category"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5"><Label>Due date <span className="text-xs text-kid-muted font-normal">optional</span></Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="h-11 rounded-xl" data-testid="task-due" /></div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="kid-btn kid-btn-ghost h-11 px-5">Cancel</button>
          <button onClick={submit} disabled={busy} className="kid-btn kid-btn-primary h-11 px-5" data-testid="task-submit">{busy ? "Saving…" : "Create task"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
